/**
 * assessment.service.ts
 * ---------------------
 * Modules 9 + 10 — Guardrail Assessment Engine.
 *
 * Communicates with the backend assessment API:
 *   POST  /api/v1/sessions/{id}/assessment/start   — trigger pipeline
 *   GET   /api/v1/sessions/{id}/assessment/status  — poll status
 *   GET   /api/v1/sessions/{id}/assessment/results — fetch results
 *   POST  /api/v1/sessions/{id}/assessment/reset   — re-trigger
 */

import { Injectable }                    from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError }        from 'rxjs';
import { catchError }                    from 'rxjs/operators';
import { environment }                   from '../../../environments/environment';

// ---------------------------------------------------------------------------
// DTOs — mirror backend Pydantic schemas
// ---------------------------------------------------------------------------

export interface StartAssessmentRequest {
  selected_categories: string[];
}

export interface StartAssessmentResponse {
  session_id: string;
  message:    string;
}

export interface AssessmentStatusResponse {
  session_id: string;
  /** 'processing' | 'completed' | 'failed' | 'not_started' */
  status:   string;
  /** 0–100 */
  progress: number;
  message:  string;
  error:    string | null;
}

export interface ProofEntry {
  document_name:   string;
  page_number:     number;
  referenced_text: string;
}

export interface GuardrailAssessmentEntry {
  guardrail_id:     string;
  category:         string;
  /** Full guardrail requirement text */
  guardrail:        string;
  /** Security Level — PCN only, otherwise empty */
  sl:               string;
  /** 'Met' | 'Partially Met' | 'Not Met' | 'Not Applicable' */
  status:           string;
  /** LLM confidence 0–100 */
  confidence_score: number;
  justification:    string;
  proof:            ProofEntry[];
  /** Free-text override for the Related Artifacts section (set when analyst edits). */
  artifacts_text?:  string;
}

export interface AssessmentSummary {
  total_guardrails: number;
  met:              number;
  partially_met:    number;
  not_met:          number;
  not_applicable:   number;
}

export interface AssessmentResultsResponse {
  session_id:          string;
  generated_at:        string;
  model:               string;
  app_types:           string[];
  sl_level:            string | null;
  selected_categories: string[];
  application_details: Record<string, any>;
  summary:             AssessmentSummary;
  assessments:         GuardrailAssessmentEntry[];
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class AssessmentService {

  private readonly _baseUrl =
    `${environment.apiConfig.uri.replace(/\/$/, '')}/api/v1/sessions`;

  constructor(private readonly http: HttpClient) {}

  // ── Trigger assessment pipeline ─────────────────────────────────────────

  /**
   * POST /sessions/{id}/assessment/start
   * Triggers the guardrail assessment pipeline.  Returns 202 immediately.
   */
  startAssessment(
    sessionId: string,
    selectedCategories: string[],
  ): Observable<StartAssessmentResponse> {
    const body: StartAssessmentRequest = { selected_categories: selectedCategories };
    return this.http
      .post<StartAssessmentResponse>(
        `${this._baseUrl}/${sessionId}/assessment/start`,
        body,
      )
      .pipe(catchError(this._handleError));
  }

  // ── Poll status ──────────────────────────────────────────────────────────

  /**
   * GET /sessions/{id}/assessment/status
   * Returns current pipeline progress.  Poll every 2–3 s.
   */
  getStatus(sessionId: string): Observable<AssessmentStatusResponse> {
    return this.http
      .get<AssessmentStatusResponse>(
        `${this._baseUrl}/${sessionId}/assessment/status`,
      )
      .pipe(catchError(this._handleError));
  }

  // ── Fetch completed results ──────────────────────────────────────────────

  /**
   * GET /sessions/{id}/assessment/results
   * Returns the completed guardrail assessment report.
   * Returns HTTP 404 if not yet available.
   */
  getResults(sessionId: string): Observable<AssessmentResultsResponse> {
    return this.http
      .get<AssessmentResultsResponse>(
        `${this._baseUrl}/${sessionId}/assessment/results`,
      )
      .pipe(catchError(this._handleError));
  }

  // ── Reset ────────────────────────────────────────────────────────────────

  /**
   * POST /sessions/{id}/assessment/reset
   * Clears pipeline state so it can be re-triggered.
   */
  reset(sessionId: string): Observable<{ session_id: string; message: string }> {
    return this.http
      .post<{ session_id: string; message: string }>(
        `${this._baseUrl}/${sessionId}/assessment/reset`,
        {},
      )
      .pipe(catchError(this._handleError));
  }

  // ── Report save ──────────────────────────────────────────────────────────

  /**
   * POST /sessions/{id}/report/save
   * Saves the report JSON to blob storage (reports/{app_id}.json).
   */
  saveReport(
    sessionId: string,
    reportData: AssessmentResultsResponse,
  ): Observable<{ message: string; blob_path: string }> {
    return this.http
      .post<{ message: string; blob_path: string }>(
        `${this._baseUrl}/${sessionId}/report/save`,
        reportData,
      )
      .pipe(catchError(this._handleError));
  }

  // ── Report download (DOCX) ───────────────────────────────────────────────

  /**
   * GET /sessions/{id}/report/download
   * Downloads the DOCX report as a Blob.
   */
  downloadReport(sessionId: string): Observable<Blob> {
    return this.http
      .get(
        `${this._baseUrl}/${sessionId}/report/download`,
        { responseType: 'blob' },
      )
      .pipe(catchError(this._handleError));
  }

  // ── Error handler ────────────────────────────────────────────────────────

  private _handleError(err: HttpErrorResponse): Observable<never> {
    const msg =
      err.error?.detail ??
      err.error?.message ??
      err.message ??
      'An unknown error occurred.';
    return throwError(() => new Error(msg));
  }
}
